import { Eye, EyeOff } from "lucide-react";
import {
  FieldTitle,
  useInput,
  useResourceContext,
  useTranslate,
  type InputProps,
} from "ra-core";
import { useState, type ComponentProps } from "react";

import {
  FormControl,
  FormError,
  FormField,
  FormLabel,
} from "@/components/admin/form";
import { InputHelperText } from "@/components/admin/input-helper-text";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * Botón «Mostrar / Ocultar contraseña» (petición de Robert, 01/10/2026). Va dentro del
 * campo, a la derecha; con teclado se llega con Tab y se pulsa con Intro o Espacio.
 */
const BotonVerContrasena = ({
  visible,
  onCambiar,
}: {
  visible: boolean;
  onCambiar: () => void;
}) => {
  const translate = useTranslate();
  const texto = translate(
    visible
      ? "nevora.login.ocultar_contrasena"
      : "nevora.login.mostrar_contrasena",
  );
  return (
    <button
      type="button"
      onClick={onCambiar}
      aria-label={texto}
      aria-pressed={visible}
      title={texto}
      className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-md text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {visible ? (
        <EyeOff className="size-4" aria-hidden />
      ) : (
        <Eye className="size-4" aria-hidden />
      )}
    </button>
  );
};

/** Input de contraseña con el botón de mostrar/ocultar, para formularios de react-hook-form. */
export const InputContrasena = ({
  className,
  ...props
}: Omit<ComponentProps<"input">, "type">) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <Input
        {...props}
        type={visible ? "text" : "password"}
        className={cn("pr-11", className)}
      />
      <BotonVerContrasena
        visible={visible}
        onCambiar={() => setVisible((v) => !v)}
      />
    </div>
  );
};

/**
 * Campo de contraseña para formularios de react-admin (`Form`): igual que `TextInput`
 * (etiqueta, ayuda, error) pero con el botón de mostrar/ocultar.
 */
export const CampoContrasena = (
  props: InputProps & Omit<ComponentProps<"input">, "type" | "defaultValue">,
) => {
  const resource = useResourceContext(props);
  const {
    label,
    source,
    className,
    helperText,
    validate: _validate,
    format: _format,
    parse: _parse,
    defaultValue: _defaultValue,
    ...rest
  } = props;
  const { id, field, isRequired } = useInput(props);
  const [visible, setVisible] = useState(false);
  return (
    <FormField id={id} className={className} name={field.name}>
      {label !== false && (
        <FormLabel>
          <FieldTitle
            label={label}
            source={source}
            resource={resource}
            isRequired={isRequired}
          />
        </FormLabel>
      )}
      <div className="relative">
        <FormControl>
          <Input
            {...rest}
            {...field}
            type={visible ? "text" : "password"}
            className="pr-11"
          />
        </FormControl>
        <BotonVerContrasena
          visible={visible}
          onCambiar={() => setVisible((v) => !v)}
        />
      </div>
      <InputHelperText helperText={helperText} />
      <FormError />
    </FormField>
  );
};
